import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';

// Scroll the window back to top whenever the route changes.
// Without this, navigating from the footer (e.g. clicking "Privacy") keeps the
// scroll position at the bottom of the new page, which feels broken.
const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // 'auto' (instant) — smooth scrolling here makes route transitions look janky
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);
  return null;
};

// Context
import { AuthProvider, AdminAuthProvider, useAdminAuth, useAuth } from './context/AuthContext';

// Layout
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import CartSidebar from './components/common/CartSidebar';
import AdminLayout from './components/admin/AdminLayout';

// Shop Pages
import HomePage from './pages/shop/HomePage';
import ShopPage from './pages/shop/ShopPage';
import ProductPage from './pages/shop/ProductPage';
import CheckoutPage from './pages/shop/CheckoutPage';
import { OrderSuccessPage, OrderCancelPage } from './pages/shop/OrderPages';
import { LoginPage, RegisterPage } from './pages/shop/AuthPages';
import AccountPage from './pages/shop/AccountPage';
import AboutPage from './pages/shop/AboutPage';
import ContactPage from './pages/shop/ContactPage';
import LegalPage from './pages/shop/LegalPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCategories from './pages/admin/AdminCategories';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminReviews from './pages/admin/AdminReviews';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAccounts from './pages/admin/AdminAccounts';

// Detect if we're running on the admin subdomain (e.g. admin.qayakw.com)
const isAdminSubdomain = () =>
  typeof window !== 'undefined' &&
  /^admin\./i.test(window.location.hostname);

// Root route: send admin-subdomain visitors straight to /admin, everyone else to the shop
const RootRoute = () =>
  isAdminSubdomain()
    ? <Navigate to="/admin" replace />
    : <ShopLayout><HomePage /></ShopLayout>;

// Catch-all: same logic — admin subdomain visitors should land on the admin area
const NotFoundRoute = () =>
  <Navigate to={isAdminSubdomain() ? '/admin' : '/'} replace />;

// Protected Routes
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" />;
};

const RequireAdmin = ({ children, superadminOnly = false }) => {
  const { admin, loading } = useAdminAuth();
  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" />;
  if (superadminOnly && admin.role !== 'superadmin') return <Navigate to="/admin" />;
  return children;
};

// Shop Layout wrapper
const ShopLayout = ({ children }) => (
  <div style={{ paddingTop: '80px' }}>
    <Navbar />
    <CartSidebar />
    {children}
    <Footer />
  </div>
);

const App = () => (
  <AuthProvider>
    <AdminAuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontFamily: 'var(--font-sans)', fontSize: '0.9rem' } }} />

        <Routes>
          {/* ─── SHOP ROUTES ───────────────────────────── */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/shop" element={<ShopLayout><ShopPage /></ShopLayout>} />
          <Route path="/product/:slug" element={<ShopLayout><ProductPage /></ShopLayout>} />
          <Route path="/checkout" element={<ShopLayout><CheckoutPage /></ShopLayout>} />
          <Route path="/checkout/success" element={<ShopLayout><OrderSuccessPage /></ShopLayout>} />
          <Route path="/checkout/cancel" element={<ShopLayout><OrderCancelPage /></ShopLayout>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/account" element={<RequireAuth><ShopLayout><AccountPage /></ShopLayout></RequireAuth>} />
          <Route path="/about" element={<ShopLayout><AboutPage /></ShopLayout>} />
          <Route path="/contact" element={<ShopLayout><ContactPage /></ShopLayout>} />
          <Route path="/privacy" element={<ShopLayout><LegalPage keyPrefix="privacy" /></ShopLayout>} />
          <Route path="/faq" element={<ShopLayout><LegalPage keyPrefix="faq" /></ShopLayout>} />
          <Route path="/shipping" element={<ShopLayout><LegalPage keyPrefix="shipping" /></ShopLayout>} />

          {/* ─── ADMIN ROUTES ──────────────────────────── */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireAdmin><AdminLayout><AdminDashboard /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/orders" element={<RequireAdmin><AdminLayout><AdminOrders /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/products" element={<RequireAdmin><AdminLayout><AdminProducts /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/categories" element={<RequireAdmin><AdminLayout><AdminCategories /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/users" element={<RequireAdmin><AdminLayout><AdminCustomers /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/coupons" element={<RequireAdmin><AdminLayout><AdminCoupons /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/reviews" element={<RequireAdmin><AdminLayout><AdminReviews /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/settings" element={<RequireAdmin><AdminLayout><AdminSettings /></AdminLayout></RequireAdmin>} />
          <Route path="/admin/accounts" element={<RequireAdmin superadminOnly><AdminLayout><AdminAccounts /></AdminLayout></RequireAdmin>} />

          {/* 404 */}
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </BrowserRouter>
    </AdminAuthProvider>
  </AuthProvider>
);

export default App;
